import { Component, OnDestroy, OnInit } from '@angular/core';
import { NavigationEnd, Router, ActivatedRouteSnapshot } from '@angular/router';
import { Title } from '@angular/platform-browser';
import { Subscription } from 'rxjs';
import { filter } from 'rxjs/operators';
import { NzNotificationService } from 'ng-zorro-antd/notification';
import { LoginService } from './shared/services/login.service';
import { AppInsightsService } from './shared/services/app_insights.service';
import { environment } from '../environments/environment';
import { VersionCheckService } from './shared/services/version-check.service';
import { AuthStateService } from './shared/services/auth_state.service';
import { AssessmentTaskTimezoneObserverService } from './shared/services/assessment_task_timezone_observer.service';
import { AssessmentAssignmentRefreshService } from './shared/services/assessment_assignment_refresh.service';

@Component({
    selector: 'app-root',
    templateUrl: './app.component.html'
})
export class AppComponent implements OnInit, OnDestroy {
    private static readonly APP_TITLE_PREFIX = 'ComplyB4';
    private readonly subscriptions = new Subscription();

    constructor(
        private loginService: LoginService,
        private router: Router,
        private titleService: Title,
        private notification: NzNotificationService,
        private appInsights: AppInsightsService,
        private versionCheck: VersionCheckService,
        private authState: AuthStateService,
        private taskTimezoneObserver: AssessmentTaskTimezoneObserverService,
        private assignmentRefresh: AssessmentAssignmentRefreshService
    ) {
        this.subscriptions.add(
            this.loginService.sessionEnded.subscribe((event) => {
                if (event?.message) {
                    this.notification.create('info', 'Session update', event.message, {
                        nzDuration: 4000
                    });
                }
                if (event?.method !== 'BUTTON') {
                    this.authState.setInactivityRedirect(this.authState.getCurrentUrl());
                }
                this.router.navigate(['/authentication/login']);
            })
        );

        this.subscriptions.add(
            this.router.events
                .pipe(filter((event): event is NavigationEnd => event instanceof NavigationEnd))
                .subscribe((event) => {
                    const pageName = this.derivePageName(event.urlAfterRedirects);
                    this.appInsights.trackPageView(pageName, event.urlAfterRedirects);
                    this.updateDocumentTitle();
                })
        );
    }

    ngOnInit(): void {
        this.updateDocumentTitle();
        this.taskTimezoneObserver.start();
        this.assignmentRefresh.start();

        if (!environment.versionCheckEnabled) {
            return;
        }
        // Check every 60 seconds; adjust as you like
        this.versionCheck.startPolling(60_000);
    }

    ngOnDestroy(): void {
        this.taskTimezoneObserver.stop();
        this.assignmentRefresh.stop();
        this.versionCheck.stopPolling();
        this.subscriptions.unsubscribe();
    }

    private derivePageName(url: string): string {
        const cleaned = url.split('?')[0].replace(/^\/+/, '');
        return cleaned || 'home';
    }

    private updateDocumentTitle(): void {
        const routeTitle = this.resolveRouteTitle(this.router.routerState.snapshot.root);
        const normalizedRouteTitle = this.normalizeRouteTitleForBrowser(routeTitle);
        const documentTitle = normalizedRouteTitle
            ? `${AppComponent.APP_TITLE_PREFIX}: ${normalizedRouteTitle}`
            : AppComponent.APP_TITLE_PREFIX;

        this.titleService.setTitle(documentTitle);
    }

    private resolveRouteTitle(route: ActivatedRouteSnapshot | null): string | null {
        if (!route) {
            return null;
        }

        let current: ActivatedRouteSnapshot | null = route;
        while (current.firstChild) {
            current = current.firstChild;
        }

        const title = current.data?.['title'];
        return typeof title === 'string' && title.trim() ? title.trim() : null;
    }

    private normalizeRouteTitleForBrowser(routeTitle: string | null): string {
        const normalizedTitle = typeof routeTitle === 'string' ? routeTitle.trim() : '';
        if (!normalizedTitle) {
            return '';
        }

        const normalizedKey = normalizedTitle.toLowerCase();
        if (normalizedKey === 'pci dss roc') {
            return 'ROC Assessment';
        }

        if (normalizedKey.startsWith('pci dss saq')) {
            return 'SAQ Assessment';
        }

        return normalizedTitle;
    }
}
